/**
 * Reports Page
 * Central hub for accessing different types of reports and analytics
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Button } from '../../components/common/Button';
import { Card, CardHeader, CardContent } from '../../components/common/Card';
import { APP_PERMISSIONS } from '../../constants';
import { usePermissions } from '../../hooks/usePermissions';
import { get } from '../../services/apiClient';
import {
  evaluationService,
  type EvaluationStats,
} from '../../services/evaluationService';
import { useAppSelector } from '../../store/hooks';
import { UserRole } from '../../types';
import { cn } from '../../utils';

interface ReportsPageProps {
  className?: string;
}

type ReportAction =
  | { kind: 'navigate'; path: string }
  | { kind: 'export'; exportKey: 'evaluation-stats' | 'budget-summary' | 'intervention-analytics' }
  | { kind: 'none' };

interface ReportItem {
  id: string;
  title: string;
  description: string;
  action: ReportAction;
  type: 'navigation' | 'generation' | 'dashboard';
  status: 'available' | 'coming_soon' | 'beta';
}

interface ReportCategory {
  id: string;
  title: string;
  description: string;
  icon: string;
  roles: UserRole[];
  reports: ReportItem[];
}

const currentFiscalYear = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  return now.getMonth() >= 6 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
};

const createReportCategories = (
  canExportBudget: boolean
): ReportCategory[] => [
  {
    id: 'evaluations',
    title: 'Evaluation Reports',
    description:
      'Teacher evaluation reports, scoring analysis, and performance tracking',
    icon: '📋',
    roles: [
      UserRole.TEACHER,
      UserRole.SCHOOL_ADMIN,
      UserRole.DISTRICT_ADMIN,
      UserRole.EVALUATOR,
      UserRole.PRINCIPAL,
      UserRole.ASSISTANT_PRINCIPAL,
    ],
    reports: [
      {
        id: 'evaluation-overview',
        title: 'Evaluation Overview',
        description: 'View and manage all evaluations with filtering and search',
        action: { kind: 'navigate', path: '/evaluations' },
        type: 'navigation',
        status: 'available',
      },
      {
        id: 'individual-reports',
        title: 'Individual Evaluation Reports',
        description:
          'Open an evaluation and use View Report for detailed JSON results',
        action: { kind: 'navigate', path: '/evaluations' },
        type: 'navigation',
        status: 'available',
      },
      {
        id: 'performance-summary',
        title: 'Performance Summary Report',
        description:
          'Download aggregate evaluation statistics for your scope (JSON)',
        action: { kind: 'export', exportKey: 'evaluation-stats' },
        type: 'generation',
        status: 'available',
      },
      {
        id: 'rubric-analysis',
        title: 'Rubric Analysis',
        description:
          'Domain-level rubric breakdown across evaluations (not yet available)',
        action: { kind: 'none' },
        type: 'generation',
        status: 'coming_soon',
      },
    ],
  },
  {
    id: 'basic',
    title: 'Basic Reports',
    description: 'Essential reports and dashboards available to all users',
    icon: '📄',
    roles: [
      UserRole.TEACHER,
      UserRole.SCHOOL_ADMIN,
      UserRole.DISTRICT_ADMIN,
      UserRole.EVALUATOR,
      UserRole.PRINCIPAL,
      UserRole.ASSISTANT_PRINCIPAL,
    ],
    reports: [
      {
        id: 'dashboard',
        title: 'Dashboard Overview',
        description: 'Main dashboard with key metrics and quick actions',
        action: { kind: 'navigate', path: '/dashboard' },
        type: 'navigation',
        status: 'available',
      },
      {
        id: 'evaluations-basic',
        title: 'Evaluations',
        description: 'Access evaluation system and reports',
        action: { kind: 'navigate', path: '/evaluations' },
        type: 'navigation',
        status: 'available',
      },
    ],
  },
  {
    id: 'analytics',
    title: 'Student Analytics',
    description: 'Predictive analytics, risk assessment, and intervention tracking',
    icon: '📊',
    roles: [
      UserRole.SCHOOL_ADMIN,
      UserRole.DISTRICT_ADMIN,
      UserRole.EVALUATOR,
      UserRole.PRINCIPAL,
      UserRole.ASSISTANT_PRINCIPAL,
    ],
    reports: [
      {
        id: 'student-success',
        title: 'Student Success Analytics',
        description: 'Predictive analytics and risk assessment dashboard',
        action: { kind: 'navigate', path: '/analytics' },
        type: 'dashboard',
        status: 'available',
      },
      {
        id: 'intervention-tracking',
        title: 'Intervention Tracking',
        description: 'Monitor intervention effectiveness and student progress',
        action: { kind: 'navigate', path: '/interventions' },
        type: 'dashboard',
        status: 'available',
      },
      {
        id: 'risk-assessment',
        title: 'Intervention Analytics Export',
        description: 'Download intervention program analytics (JSON)',
        action: { kind: 'export', exportKey: 'intervention-analytics' },
        type: 'generation',
        status: 'available',
      },
    ],
  },
  {
    id: 'financial',
    title: 'Financial Reports',
    description: 'Budget planning, financial analysis, and resource allocation',
    icon: '💰',
    roles: [
      UserRole.SCHOOL_ADMIN,
      UserRole.DISTRICT_ADMIN,
      UserRole.PRINCIPAL,
      UserRole.ASSISTANT_PRINCIPAL,
    ],
    reports: [
      {
        id: 'budget-planning',
        title: 'Budget Planning Dashboard',
        description: 'Comprehensive budget planning and financial reporting tools',
        action: { kind: 'navigate', path: '/budget' },
        type: 'dashboard',
        status: 'available',
      },
      {
        id: 'expense-analysis',
        title: 'Budget Summary Export',
        description: canExportBudget
          ? 'Download budget summary report for the current fiscal year (JSON)'
          : 'Budget exports require view budget or expense approval permission',
        action: canExportBudget
          ? { kind: 'export', exportKey: 'budget-summary' }
          : { kind: 'none' },
        type: 'generation',
        status: canExportBudget ? 'available' : 'coming_soon',
      },
    ],
  },
  {
    id: 'administrative',
    title: 'Administrative Reports',
    description: 'System usage, user management, and operational reports',
    icon: '⚙️',
    roles: [
      UserRole.SCHOOL_ADMIN,
      UserRole.DISTRICT_ADMIN,
      UserRole.PRINCIPAL,
      UserRole.ASSISTANT_PRINCIPAL,
    ],
    reports: [
      {
        id: 'user-activity',
        title: 'User Activity Report',
        description: 'Track user engagement and system usage patterns',
        action: { kind: 'none' },
        type: 'generation',
        status: 'coming_soon',
      },
      {
        id: 'system-usage',
        title: 'System Usage Analytics',
        description: 'Platform usage statistics and performance metrics',
        action: { kind: 'none' },
        type: 'dashboard',
        status: 'coming_soon',
      },
      {
        id: 'inventory-reports',
        title: 'Inventory Reports',
        description: 'School supplies and equipment tracking reports',
        action: { kind: 'navigate', path: '/inventory' },
        type: 'dashboard',
        status: 'available',
      },
    ],
  },
];

const downloadJson = (filename: string, data: unknown) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export const ReportsPage: React.FC<ReportsPageProps> = ({ className }) => {
  const { user } = useAppSelector(state => state.auth);
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [selectedCategory, setSelectedCategory] = useState<string>('evaluations');
  const [stats, setStats] = useState<EvaluationStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [exportingKey, setExportingKey] = useState<string | null>(null);

  const canExportBudget =
    hasPermission(APP_PERMISSIONS.VIEW_BUDGET) ||
    hasPermission(APP_PERMISSIONS.APPROVE_EXPENSES);

  const reportCategories = useMemo(
    () => createReportCategories(canExportBudget),
    [canExportBudget]
  );

  const isTeacher = user?.role === UserRole.TEACHER;

  const availableCategories = reportCategories.filter(category =>
    category.roles.includes(user?.role as UserRole)
  );

  const finalAvailableCategories = useMemo(() => {
    if (availableCategories.length > 0) {
      return availableCategories;
    }
    return user ? reportCategories.filter(cat => cat.id === 'basic') : [];
  }, [availableCategories, user, reportCategories]);

  useEffect(() => {
    if (finalAvailableCategories.length > 0) {
      const currentExists = finalAvailableCategories.some(
        cat => cat.id === selectedCategory
      );
      if (!currentExists && finalAvailableCategories[0]) {
        setSelectedCategory(finalAvailableCategories[0].id);
      }
    }
  }, [finalAvailableCategories, selectedCategory]);

  useEffect(() => {
    if (!user) {
      setStats(null);
      setStatsLoading(false);
      return;
    }

    let cancelled = false;
    setStatsLoading(true);
    setStatsError(null);

    evaluationService
      .getEvaluationStats()
      .then(data => {
        if (!cancelled) {
          setStats(data);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setStatsError(
            err instanceof Error ? err.message : 'Could not load evaluation stats.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setStatsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  const runExport = useCallback(
    async (exportKey: ReportAction & { kind: 'export' }) => {
      setActionError(null);
      setExportingKey(exportKey.exportKey);
      try {
        if (exportKey.exportKey === 'evaluation-stats') {
          const data = await evaluationService.getEvaluationStats();
          downloadJson('evaluation-performance-summary.json', data);
          return;
        }
        if (exportKey.exportKey === 'intervention-analytics') {
          const data = await get<unknown>('/interventions/analytics');
          downloadJson('intervention-analytics.json', data);
          return;
        }
        if (exportKey.exportKey === 'budget-summary') {
          const fiscalYear = currentFiscalYear();
          const data = await get<unknown>(
            `/api/budget/reports/summary?fiscal_year=${encodeURIComponent(
              fiscalYear
            )}&format=json`
          );
          downloadJson(`budget-summary-${fiscalYear}.json`, data);
        }
      } catch (err) {
        setActionError(
          err instanceof Error ? err.message : 'The report could not be exported.'
        );
      } finally {
        setExportingKey(null);
      }
    },
    []
  );

  const handleReportAction = useCallback(
    async (action: ReportAction) => {
      if (action.kind === 'none') {
        return;
      }
      if (action.kind === 'navigate') {
        navigate(action.path);
        return;
      }
      await runExport(action);
    },
    [navigate, runExport]
  );

  const selectedCategoryData =
    finalAvailableCategories.find(cat => cat.id === selectedCategory) ||
    finalAvailableCategories[0];

  if (finalAvailableCategories.length === 0) {
    return (
      <div className={cn('space-y-6', className)}>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
            <p className="text-gray-600 mt-1">
              Access comprehensive reports and analytics
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">No Reports Available</h2>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <div className="text-gray-400 mb-4">
                <span className="text-4xl">📊</span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Reports Not Available
              </h3>
              <p className="text-gray-600 mb-4">
                {!user
                  ? 'Please log in to access reports and analytics.'
                  : `Reports are not available for your current role: ${user.role}`}
              </p>
              {!user && (
                <Button
                  variant="primary"
                  onClick={() => navigate('/login')}
                  className="min-h-[44px]"
                >
                  Log In
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getStatusBadge = (status: ReportItem['status']) => {
    switch (status) {
      case 'available':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Available
          </span>
        );
      case 'coming_soon':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            Coming Soon
          </span>
        );
      case 'beta':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            Beta
          </span>
        );
      default:
        return null;
    }
  };

  const getActionIcon = (type: ReportItem['type']) => {
    switch (type) {
      case 'navigation':
        return '🔗';
      case 'generation':
        return '📄';
      case 'dashboard':
        return '📊';
      default:
        return '📋';
    }
  };

  const getButtonLabel = (report: ReportItem) => {
    if (report.status === 'coming_soon') {
      return 'Not available yet';
    }
    if (report.action.kind === 'export') {
      return exportingKey === report.action.exportKey ? 'Exporting…' : 'Download JSON';
    }
    if (report.type === 'navigation') {
      return 'View';
    }
    if (report.type === 'dashboard') {
      return 'Open Dashboard';
    }
    return 'Generate';
  };

  return (
    <div className={cn('space-y-6', className)}>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-gray-600 mt-1">
            Access comprehensive reports and analytics for your{' '}
            {isTeacher ? 'evaluations' : 'school data'}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold">Evaluation snapshot</h2>
          <p className="text-sm text-gray-600">
            Live aggregate stats from the evaluation service for your account scope
          </p>
        </CardHeader>
        <CardContent>
          {statsLoading && (
            <p className="text-sm text-gray-600">Loading evaluation statistics…</p>
          )}
          {!statsLoading && statsError && (
            <p className="text-sm text-red-600" role="alert">
              {statsError}
            </p>
          )}
          {!statsLoading && stats && !statsError && (
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <dt className="text-gray-500">Total evaluations</dt>
                <dd className="text-xl font-semibold text-gray-900">
                  {stats.total_evaluations}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Completed</dt>
                <dd className="text-xl font-semibold text-gray-900">
                  {stats.completed_evaluations}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Completion rate</dt>
                <dd className="text-xl font-semibold text-gray-900">
                  {stats.completion_rate.toFixed(1)}%
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Average score</dt>
                <dd className="text-xl font-semibold text-gray-900">
                  {stats.score_statistics.count > 0
                    ? stats.score_statistics.average_score.toFixed(1)
                    : '—'}
                </dd>
              </div>
            </dl>
          )}
        </CardContent>
      </Card>

      {actionError && (
        <p className="text-sm text-red-600" role="alert">
          {actionError}
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {finalAvailableCategories.map(category => (
          <button
            key={category.id}
            type="button"
            onClick={() => setSelectedCategory(category.id)}
            className={cn(
              'p-4 rounded-lg border-2 transition-all text-left',
              selectedCategory === category.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            )}
          >
            <div className="flex items-center space-x-3">
              <span className="text-2xl">{category.icon}</span>
              <div>
                <h3 className="font-semibold text-gray-900">{category.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{category.description}</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {selectedCategoryData && (
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-3">
              <span className="text-2xl">{selectedCategoryData.icon}</span>
              <div>
                <h2 className="text-lg font-semibold">{selectedCategoryData.title}</h2>
                <p className="text-gray-600">{selectedCategoryData.description}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {selectedCategoryData.reports.map(report => {
                const isDisabled =
                  report.status === 'coming_soon' ||
                  report.action.kind === 'none' ||
                  (report.action.kind === 'export' &&
                    exportingKey === report.action.exportKey);

                return (
                  <div
                    key={report.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">{getActionIcon(report.type)}</span>
                        <h3 className="font-semibold text-gray-900">{report.title}</h3>
                      </div>
                      {getStatusBadge(report.status)}
                    </div>

                    <p className="text-sm text-gray-600 mb-4">{report.description}</p>

                    <Button
                      variant={report.status === 'available' ? 'primary' : 'secondary'}
                      onClick={() => void handleReportAction(report.action)}
                      disabled={isDisabled}
                      className="w-full"
                    >
                      {getButtonLabel(report)}
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {isTeacher && (
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Quick Access</h2>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Button
                variant="primary"
                onClick={() => navigate('/evaluations')}
                className="flex items-center justify-center space-x-2 p-4"
              >
                <span>📋</span>
                <span>View My Evaluations</span>
              </Button>
              <Button
                variant="secondary"
                onClick={() => navigate('/evaluations/create')}
                className="flex items-center justify-center space-x-2 p-4"
              >
                <span>➕</span>
                <span>Submit New Video</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReportsPage;
